import { MOCK_CAR_MAKES, type CarMake } from '@/app/lib/mock-db';
import { getKnownCarBrandLogo } from '@/lib/car-brand-logos';

const RAW_API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function resolveApiBaseUrl(): string {
  try {
    const parsed = new URL(RAW_API);
    if (typeof window !== 'undefined' && isLocalHost(parsed.hostname) && !isLocalHost(window.location.hostname)) {
      parsed.hostname = window.location.hostname;
      parsed.port = '8000';
      return parsed.origin;
    }
    return parsed.origin;
  } catch {
    return RAW_API;
  }
}

function resolveLogoUrl(raw: unknown, makeCode: string, nameEn: string, nameAr: string): string {
  const fallback = getKnownCarBrandLogo(makeCode, nameEn, nameAr) || '/maknna_logo.png';
  if (!raw || typeof raw !== 'string') return fallback;
  const value = raw.trim();
  if (!value) return fallback;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/')) return `${resolveApiBaseUrl()}${value}`;
  return fallback;
}

function normalizeMake(raw: any, idx: number): CarMake | null {
  const idValue = raw?.code ?? raw?.id;
  const id = idValue !== undefined && idValue !== null ? String(idValue).trim() : '';
  if (!id) return null;

  const nameAr = String(raw?.name_ar || raw?.name || `ماركة ${id}`).trim();
  const nameEn = String(raw?.name || raw?.name_en || raw?.name_ar || `Make ${id}`).trim();
  const country = ['japanese', 'korean', 'german', 'others'].includes(raw?.country_group)
    ? raw.country_group
    : 'others';

  return {
    id,
    name_ar: nameAr,
    name_en: nameEn,
    country_group: country,
    logo_url: resolveLogoUrl(raw?.logo_url ?? raw?.logo, id, nameEn, nameAr),
    is_active: raw?.is_active !== false,
    sort_order: Number(raw?.sort_order ?? idx + 1),
  };
}

function normalizeKey(value: unknown): string {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]/g, '');
}

function mergeMakes(primary: CarMake[], fallback: CarMake[]): CarMake[] {
  const merged: CarMake[] = [];
  const seen = new Set<string>();

  const addMake = (make: CarMake) => {
    const keys = [make.id, make.name_en, make.name_ar].map(normalizeKey).filter(Boolean);
    if (keys.some((key) => seen.has(key))) return;
    keys.forEach((key) => seen.add(key));
    merged.push(make);
  };

  primary.forEach(addMake);
  fallback.forEach(addMake);
  return merged.sort((a, b) => (a.sort_order - b.sort_order) || a.name_en.localeCompare(b.name_en));
}

export async function fetchCarMakes(): Promise<CarMake[]> {
  try {
    const res = await fetch(`${resolveApiBaseUrl()}/api/marketplace/makes/?is_active=true`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return MOCK_CAR_MAKES;
    const payload = await res.json();
    const rows = Array.isArray(payload) ? payload : (payload?.results || []);
    const makes = rows
      .map(normalizeMake)
      .filter((m: CarMake | null): m is CarMake => Boolean(m))
      .filter((m: CarMake) => m.is_active)
      .sort((a: CarMake, b: CarMake) => (a.sort_order - b.sort_order) || a.name_en.localeCompare(b.name_en));

    return mergeMakes(makes, MOCK_CAR_MAKES);
  } catch {
    return MOCK_CAR_MAKES;
  }
}
